
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';
import { useCart } from '../../../context/CartContext';
import { useQuickView } from '../../../context/QuickViewContext';
import { shopManuals } from '../../../mocks/shopProducts';

export default function ShopManuals() {
  const { ref, isVisible } = useScrollAnimation();
  const { addToCart } = useCart();
  const { openQuickView } = useQuickView();

  const handleAdd = (m: typeof shopManuals[0]) => {
    addToCart({
      id: `manual-${m.id}`,
      name: m.title,
      price: m.price,
      originalPrice: m.originalPrice,
      image: m.image,
      type: 'manual',
      description: m.description,
    });
  };

  const handleQuickView = (m: typeof shopManuals[0]) => {
    const diffLabels = ['Beginner', 'Intermediate', 'Advanced'];
    openQuickView({
      id: `manual-${m.id}`,
      name: m.title,
      description: m.description,
      price: m.price,
      originalPrice: m.originalPrice,
      image: m.image,
      type: 'manual',
      badge: m.badge,
      details: [
        { label: 'Recipes', value: `${m.recipes} cocktails`, icon: 'ri-goblet-line' },
        { label: 'Techniques', value: `${m.techniques} methods`, icon: 'ri-tools-line' },
        { label: 'Pages', value: `${m.pages} pages`, icon: 'ri-file-list-line' },
        { label: 'Difficulty', value: diffLabels[m.difficulty - 1], icon: 'ri-bar-chart-line' },
      ],
      extras: ['Digital PDF included', 'Free updates', 'Recipe index'],
    });
  };

  const renderDifficulty = (level: number) => (
    <div className="flex gap-0.5">
      {[1, 2, 3].map((i) => (
        <i key={i} className={`ri-goblet-fill text-xs ${i <= level ? 'text-amber-500' : 'text-stone-200'}`}></i>
      ))}
    </div>
  );

  return (
    <section id="shop-manuals" className="py-24 bg-white scroll-mt-20">
      <div ref={ref} className="max-w-7xl mx-auto px-6">
        <div className={`flex flex-col lg:flex-row lg:items-end lg:justify-between mb-14 gap-6 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-400 to-teal-300 flex items-center justify-center shadow-lg shadow-emerald-500/20">
                <i className="ri-book-open-line text-white"></i>
              </div>
              <span className="text-amber-600 text-xs font-semibold tracking-[0.2em] uppercase">Learn at Your Own Pace</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-display font-normal text-stone-900">Training Manuals</h2>
          </div>
          <p className="text-stone-500 max-w-md leading-relaxed text-sm">
            Comprehensive guides covering 200+ cocktail recipes, techniques, and bar management secrets.
          </p>
        </div>

        <div className={`grid md:grid-cols-2 lg:grid-cols-3 gap-7 transition-all duration-700 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} data-product-shop>
          {shopManuals.map((m) => (
            <div key={m.id} className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500 border border-stone-100 hover:border-amber-200/50">
              <div className="relative p-6 bg-gradient-to-br from-stone-100 to-stone-50">
                <div className="absolute top-3 right-3 px-3 py-1 bg-gradient-to-r from-amber-400 to-yellow-300 text-stone-900 text-[10px] font-semibold rounded-full shadow-lg">
                  {m.badge}
                </div>
                <div className="flex justify-center">
                  <div className="w-36 h-48">
                    <img src={m.image} alt={m.title} className="w-full h-full object-contain group-hover:scale-105 group-hover:-rotate-2 transition-all duration-500" />
                  </div>
                </div>
                <button
                  onClick={() => handleQuickView(m)}
                  className="absolute bottom-3 left-1/2 -translate-x-1/2 px-3 py-1.5 bg-white/95 backdrop-blur-sm rounded-full shadow-lg flex items-center gap-1.5 opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-300 cursor-pointer hover:bg-stone-900 hover:text-white"
                >
                  <i className="ri-eye-line text-xs"></i>
                  <span className="text-[10px] font-semibold whitespace-nowrap">Quick View</span>
                </button>
              </div>

              <div className="p-6">
                <div className="flex items-center justify-between mb-3">
                  {renderDifficulty(m.difficulty)}
                  <span className="text-[10px] text-stone-400 font-medium">{m.pages} pages</span>
                </div>
                <h3 className="text-lg font-serif font-semibold text-stone-900 mb-1.5">{m.title}</h3>
                <p className="text-stone-500 text-sm mb-4 leading-relaxed line-clamp-2">{m.description}</p>

                <div className="flex items-center gap-4 text-xs text-stone-400 mb-4">
                  <span className="flex items-center gap-1">
                    <i className="ri-file-list-line text-amber-500"></i>{m.recipes} Recipes
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-tools-line text-amber-500"></i>{m.techniques} Techniques
                  </span>
                </div>

                <div className="flex items-center justify-between pt-4 border-t border-stone-100">
                  <div className="flex items-center gap-2">
                    <span className="text-2xl font-display text-stone-900">${m.price}</span>
                    <span className="text-stone-300 line-through text-xs">${m.originalPrice}</span>
                  </div>
                  <button
                    onClick={() => handleAdd(m)}
                    className="px-4 py-2 bg-stone-900 text-white text-sm font-medium rounded-full hover:bg-gradient-to-r hover:from-amber-400 hover:to-yellow-300 hover:text-stone-900 transition-all whitespace-nowrap flex items-center gap-1.5 cursor-pointer"
                  >
                    <i className="ri-shopping-cart-line text-sm"></i>
                    Add to Cart
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
